import { useEffect, useMemo } from "react";
import { useRouter } from "next/router";
import { useTranslations } from "next-intl";
import { useLayoutContext } from "@context/layout";
import { Meta } from "@layouts/page";
import useRequestImmutable from "@shared/hooks/useRequestImmutable";
import { scrollTop } from "@utils/scroll";
import howToLearnApi from "../helpers/howToLearnApi";
import { HOW_TO_LEARN_PAGE_LIMIT } from "../helpers/constants";
import {
  FaqListObject,
  howToLearnItemListObject,
  imageObject,
  itemPage,
} from "@utils/jsonLd";
import { SITE_URL } from "@utils/constants";
import { resolveThumbnailSources } from "utils/product-image";

const REQUEST_PAGINATION_PARAMS = {
  page: 1,
  limit: HOW_TO_LEARN_PAGE_LIMIT,
};

const useDetailPageConfig = () => {
  const router = useRouter();
  const t = useTranslations("meta");
  const { isAdmin } = useLayoutContext();

  const slug = router.query.slug;
  const url = SITE_URL + router.asPath;

  const { data: landingDetail, error } = useRequestImmutable(
    slug ? howToLearnApi.singleHowToLearn(String(slug)) : undefined,
    { shouldRetryOnError: false }
  );

  const detail = landingDetail?.data;
  const landingId = detail?.id;

  const { data: productsData, mutate } = useRequestImmutable(
    landingId
      ? howToLearnApi.singleHowToLearnProductV2(
          String(landingId),
          REQUEST_PAGINATION_PARAMS
        )
      : undefined,
    { shouldRetryOnError: false }
  );

  useEffect(() => {
    const timer = setTimeout(scrollTop, 100);

    return () => clearTimeout(timer);
  }, [router.asPath]);

  const meta: Meta = useMemo(
    () => ({
      title: detail?.seo_title ?? t("title"),
      description: detail?.seo_metadesc ?? t("desc"),
      image: detail?.png_image,
    }),
    [detail]
  );
  const heroImage = resolveThumbnailSources({
    svg_image: landingDetail?.data?.svg_image,
    png_image: landingDetail?.data?.png_image,
  }).src;

  const jsonLd = useMemo(() => {
    return [
      howToLearnItemListObject(landingDetail, productsData, url),
      FaqListObject(landingDetail),
      itemPage({
        url,
        imageUrl: landingDetail?.data?.svg_image,
        name: landingDetail?.data?.seo_title,
        mainContentSelector: ".howToLearnItemPageSelector",
      }),
      imageObject({
        caption: landingDetail?.data?.seo_metadesc,
        contentUrl: landingDetail?.data?.svg_image,
        width: "350",
        height: "150",
        url,
        isPartOf: url,
      }),
    ];
  }, [landingDetail, productsData, url]);

  return {
    pageConfig: {
      head: heroImage ? (
        <link rel="preload" as="image" href={heroImage} fetchPriority="high" />
      ) : null,
      meta,
      jsonLd,
    },
    error,
    mutate,
    isAdmin,
    landing: productsData,
    landingDetail,
  };
};

export default useDetailPageConfig;
